import { Dialog } from "@/components/ui/Dialog";
import { skipReasonLabel, type UploadPlan } from "@/lib/folderUpload";
import { formatBytes } from "@/lib/format";

interface UploadConfirmDialogProps {
  pending: { plan: UploadPlan; targetFolder: string } | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const MAX_SKIPPED_ROWS = 50;

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? singular : pluralForm}`;
}

/** Antes de lote grande (`needsConfirmation`): arrastar a pasta errada custa caro. */
export function UploadConfirmDialog({ pending, onCancel, onConfirm }: UploadConfirmDialogProps) {
  const plan = pending?.plan;
  const destino = pending?.targetFolder ? `«${pending.targetFolder}»` : "a raiz";

  return (
    <Dialog
      title="Enviar lote grande?"
      open={pending !== null}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
            onClick={onConfirm}
          >
            Enviar
          </button>
        </>
      }
    >
      {plan && (
        <div className="space-y-3">
          <p className="leading-relaxed">
            {`Enviar ${plural(plan.uploads.length, "arquivo", "arquivos")} (${formatBytes(plan.totalBytes)}) para ${destino}`}
            {plan.emptyFolders.length > 0 &&
              ` e criar ${plural(plan.emptyFolders.length, "pasta vazia", "pastas vazias")}`}
            .
          </p>
          <p className="text-xs leading-relaxed text-ink-400">
            Pasta com o mesmo nome é mesclada: arquivo com o mesmo caminho de um que já está lá
            vira nova versão dele, e conteúdo idêntico não duplica.
          </p>
          {(plan.skipped.length > 0 || plan.systemCount > 0) && (
            <details className="rounded border border-ink-700 px-3 py-2">
              <summary className="cursor-pointer text-xs text-amber-300">
                {[
                  plan.skipped.length > 0 ? plural(plan.skipped.length, "ignorado", "ignorados") : null,
                  plan.systemCount > 0
                    ? `${plural(plan.systemCount, "arquivo", "arquivos")} de sistema (.DS_Store e afins)`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </summary>
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {plan.skipped.slice(0, MAX_SKIPPED_ROWS).map((item, index) => (
                  <li
                    key={`${item.relativePath}:${index}`}
                    className="flex items-baseline justify-between gap-3 text-[11px]"
                  >
                    <span className="min-w-0 truncate text-ink-300" title={item.relativePath}>
                      {item.relativePath}
                    </span>
                    <span className="shrink-0 text-ink-500" title={item.detail}>
                      {skipReasonLabel(item.reason)}
                    </span>
                  </li>
                ))}
                {plan.skipped.length > MAX_SKIPPED_ROWS && (
                  <li className="text-[11px] text-ink-500">{`+ ${plan.skipped.length - MAX_SKIPPED_ROWS}`}</li>
                )}
              </ul>
            </details>
          )}
        </div>
      )}
    </Dialog>
  );
}
